import React, { useEffect, useState, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  CarouselPresentationContainer,
  CarouselPresentationContainerProps,
} from '../CarouselPresentationContainer/CarouselPresentationContainer';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import { styles } from './ProductCarousel.css';
import { Carousel, CarouselProps } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { useProductContext, withProductProvider, WithProductProviderProps } from '../context/ProductProvider';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { HeadingDivider } from '../HeadingDivider/HeadingDivider';
import { ProductFallback, ProductFallbackProps } from '../ProductFallback/ProductFallback';
import { NoResultsPlaceholder } from '../NoResultsPlaceholder/NoResultsPlaceholder';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';
import { trackViewItemList } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackViewItemList';
import { transformAlgoliaHitForTracking } from '../Algolia/transformers/transformAlgoliaHitForTracking';
import { GoToLink, GoToLinkProps } from '../GoToLink/GoToLink';
import { useEventsContext } from '../context/Events';
import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';
import type { MiniProductInfoVariant } from '../Algolia/transformers/AlgoliaHitTransformer';

type SwiperClass = Parameters<Exclude<CarouselProps['onSwiper'], undefined>>[0];
type CarouselPropsWithoutChildren = Omit<CarouselProps, 'children'>;
export type ProductCarouselProps = WithProductProviderProps<Props>;
export type PresentationProductCarouselProps = Omit<CarouselPresentationContainerProps, 'children'> &
  ProductCarouselProps;

const LOADING_PRODUCTS: Array<null> = Array.from(Array(8)).fill(null);
const DEFAULT_MAX_NUM_PRODUCTS = 20;
const DEFAULT_IN_VIEW_SCROLL_TOP_DISTANCE = '1000px'; // The distance the element should be from the top of the viewport to be considered 'in view'

type Props = CarouselPropsWithoutChildren & {
  heading?: string;
  ctaButton?: React.ReactNode;
  linkProps?: Omit<GoToLinkProps, 'className'>;
  renderProduct: (data: {
    hit: AlgoliaProductHit;
    position: number;
    trackingDescriptor: any;
    miniProductInfoVariant?: MiniProductInfoVariant;
    shouldShowNumber?: boolean;
  }) => React.ReactNode;
  miniProductInfoVariant?: MiniProductInfoVariant;
  shouldShowNumber?: boolean;
  noResultsPlaceholder?: React.ReactNode;
  productCardSkeletonProps?: ProductFallbackProps['productCardSkeletonProps'];
  carouselClassName?: string;
  shouldTrackImpressions?: boolean;
  shouldLoadOnScroll?: boolean;
  products?: Array<AlgoliaProductHit>;
  trackingDescriptor?: {
    page?: string;
    group?: string;
    category?: string;
    ecommerceExtraProps?: { list?: string; itemListSlot?: string; itemListServiceConfig?: string };
  };
  maxNumProducts?: number;
  extraSlideStart?: React.ReactNode;
};

export const ProductCarousel = ({
  heading,
  ctaButton,
  linkProps,
  renderProduct,
  noResultsPlaceholder,
  productCardSkeletonProps = {},
  onSwiper,
  className,
  products,
  carouselClassName,
  shouldTrackImpressions = false,
  shouldLoadOnScroll = false,
  trackingDescriptor = {},
  maxNumProducts = DEFAULT_MAX_NUM_PRODUCTS,
  extraSlideStart,
  miniProductInfoVariant,
  shouldShowNumber = false,
  ...rest
}: Props) => {
  const { ref: containerRef, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
    rootMargin: `${DEFAULT_IN_VIEW_SCROLL_TOP_DISTANCE} 0px`,
    skip: !shouldLoadOnScroll,
  });

  const isMobile = useBreakpoint('md', 'down');

  const carouselProps: CarouselPropsWithoutChildren = {
    spaceBetween: 10,
    slidesPerView: 'auto',
    slidesPerGroup: 2,
    slidesOffsetBefore: 2,
    slidesOffsetAfter: 2,
    showProgressBar: true,
    showArrows: !isMobile,
    showPagination: false,
    fullBleed: isMobile,
    className: carouselClassName,
    arrowPosition: 'bottom',
  };

  return (
    <div className={classNames(styles.container, className)} ref={containerRef}>
      {heading || linkProps ? (
        <div className={styles.headingContainer}>
          {heading ? <HeadingDivider heading={heading} /> : null}
          {linkProps ? <GoToLink {...linkProps} className={styles.linkDesktop} /> : null}
        </div>
      ) : null}

      {inView || !shouldLoadOnScroll ? (
        <ProductCarouselContent
          renderProduct={renderProduct}
          noResultsPlaceholder={noResultsPlaceholder}
          productCardSkeletonProps={productCardSkeletonProps}
          onSwiper={onSwiper}
          shouldTrackImpressions={shouldTrackImpressions}
          trackingDescriptor={trackingDescriptor}
          maxNumProducts={maxNumProducts}
          extraSlideStart={extraSlideStart}
          miniProductInfoVariant={miniProductInfoVariant}
          shouldShowNumber={shouldShowNumber}
          carouselProps={carouselProps}
          products={products}
          {...rest}
        />
      ) : (
        <ProductCarouselPlaceholder productCardSkeletonProps={productCardSkeletonProps} carouselProps={carouselProps} />
      )}

      {linkProps ? <GoToLink {...linkProps} className={styles.linkMobile} /> : null}
      {ctaButton ? <div className={styles.ctaButton}>{ctaButton}</div> : null}
    </div>
  );
};

export const ProductCarouselContent = withProductProvider(
  ({
    renderProduct,
    noResultsPlaceholder,
    productCardSkeletonProps = {},
    onSwiper,
    shouldTrackImpressions = false,
    trackingDescriptor = {},
    maxNumProducts = DEFAULT_MAX_NUM_PRODUCTS,
    extraSlideStart,
    miniProductInfoVariant,
    shouldShowNumber = false,
    carouselProps,
    ...rest
  }: Props & { carouselProps: CarouselPropsWithoutChildren }) => {
    const { products: allProducts = [], isLoading } = useProductContext();
    const { postEvent } = useEventsContext();
    const [swiper, setSwiper] = useState<SwiperClass | undefined>();
    const hasNoProducts = !isLoading && !allProducts?.length;
    const isMiniProduct = productCardSkeletonProps.variant === 'mini';
    const products = allProducts.slice(0, maxNumProducts);
    const { page = '', group = '', category = '', ecommerceExtraProps = {} } = trackingDescriptor;
    const { list = 'ProductCarousel', itemListSlot, itemListServiceConfig } = ecommerceExtraProps;

    const buildTrackingDescriptor = (position?: number) => ({
      page,
      group,
      category,
      currency: getStoreCurrency(),
      position,
      ecommerceExtraProps: {
        list,
        position,
        itemListSlot,
        itemListServiceConfig,
      },
    });

    useEffect(() => swiper?.update(), [swiper, products?.length, isLoading]);

    const trackedProductIds = useRef<Array<string>>([]);
    useEffect(() => {
      const filteredProducts: Array<AlgoliaProductHit> = products?.filter(
        p => p && !trackedProductIds.current.includes(p.sku)
      );
      filteredProducts.forEach(({ sku }) => trackedProductIds.current.push(sku));

      trackViewItemList({
        currency: getStoreCurrency(),
        listName: list,
        itemListSlot,
        itemListServiceConfig,
        viewedItems: transformAlgoliaHitForTracking(filteredProducts),
      });
      postEvent({ name: 'component.carousel.view', data: filteredProducts });
    }, [products, shouldTrackImpressions, list, itemListSlot, itemListServiceConfig]);

    if (hasNoProducts) {
      if (noResultsPlaceholder === null) return <></>;
      return <>{noResultsPlaceholder ?? <ProductCarouselNoResultsPlaceholder isMiniProduct={isMiniProduct} />}</>;
    }

    return (
      <div className={styles.carouselContainer}>
        <Carousel
          {...carouselProps}
          onSwiper={swiper => {
            onSwiper?.(swiper);
            setSwiper(swiper);
          }}
          {...rest}
        >
          {[extraSlideStart ?? undefined]
            .concat(
              (isLoading ? LOADING_PRODUCTS : products).slice(0, maxNumProducts).map((product, i) => (
                <ProductFallback
                  isLoading={isLoading}
                  product={product}
                  key={product?.objectID ?? i}
                  productCardSkeletonProps={{ variant: 'tablet', ...productCardSkeletonProps }}
                >
                  {product
                    ? renderProduct({
                        hit: product,
                        position: i + 1,
                        trackingDescriptor: buildTrackingDescriptor(i + 1),
                        miniProductInfoVariant,
                        shouldShowNumber,
                      })
                    : null}
                </ProductFallback>
              ))
            )
            .filter(Boolean)}
        </Carousel>
      </div>
    );
  }
);

interface ProductCarouselPlaceholderProps {
  productCardSkeletonProps: ProductFallbackProps['productCardSkeletonProps'];
  carouselProps: CarouselPropsWithoutChildren;
}

const ProductCarouselPlaceholder = ({
  productCardSkeletonProps = {},
  carouselProps,
}: ProductCarouselPlaceholderProps) => {
  return (
    <div className={styles.carouselContainer}>
      <Carousel {...carouselProps}>
        {LOADING_PRODUCTS.slice(0, DEFAULT_MAX_NUM_PRODUCTS).map((product, i) => (
          <ProductFallback
            isLoading={true}
            product={product}
            key={i}
            productCardSkeletonProps={{ variant: 'tablet', ...productCardSkeletonProps }}
          >
            {product}
          </ProductFallback>
        ))}
      </Carousel>
    </div>
  );
};

export const PresentationProductCarousel: React.VFC<PresentationProductCarouselProps> = ({
  coverImageSrc,
  coverImageAlt,
  coverContent,
  calloutBtn,
  className,
  ...rest
}) => {
  const isMiniProduct = rest.productCardSkeletonProps?.variant === 'mini';

  return (
    <CarouselPresentationContainer
      coverImageSrc={coverImageSrc}
      coverImageAlt={coverImageAlt}
      coverContent={coverContent}
      calloutBtn={calloutBtn}
    >
      <ProductCarousel
        className={classNames(styles.presentationContainerCarousel, className)}
        backgroundColor={palette.primary.light}
        outerSpacing={4}
        fullBleed={false}
        noResultsPlaceholder={<ProductCarouselNoResultsPlaceholder isMiniProduct={isMiniProduct} />}
        {...rest}
      />
    </CarouselPresentationContainer>
  );
};

const ProductCarouselNoResultsPlaceholder = ({ isMiniProduct = false }: { isMiniProduct?: boolean }) => (
  <NoResultsPlaceholder
    isMiniProduct={isMiniProduct}
    icon="LecternIllustrated"
    title="Looks like this shelf is empty"
    contents="We’re restocking this section... in the meantime check out our other deals."
  />
);
